import { InboxOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Flex, message, Upload } from 'antd'
import { PartnerWhitelistUploadResponse } from 'Modules'
import type { UploadRequestOption } from 'rc-upload/lib/interface'

import { AppAdmin } from 'api/app-admin'
import {
  PARTNER_REFERRAL_MESSAGES,
  PARTNER_WHITELIST_ACCEPT,
  PARTNER_WHITELIST_ALLOWED_EXTENSIONS,
  PARTNER_WHITELIST_MAX_SIZE_MB,
  PARTNER_WHITELIST_TEMPLATE_FILENAME,
  PARTNER_WHITELIST_TEMPLATE_HEADER,
  PARTNER_WHITELIST_TEMPLATE_SAMPLE_ADDRESSES,
} from 'constants/partner-referral'
import { logError } from 'utils/logger'

interface PartnerWhitelistUploadProps {
  /** Called after a successful upload so the list can refetch (and expose the upload for export). */
  onUploaded: (result?: PartnerWhitelistUploadResponse) => void
}

/**
 * Build the whitelist template CSV in-memory and trigger a browser download.
 * Generated client-side (no static asset) so it works identically on dev and prod.
 */
const downloadWhitelistTemplate = () => {
  const csv = [
    PARTNER_WHITELIST_TEMPLATE_HEADER,
    ...PARTNER_WHITELIST_TEMPLATE_SAMPLE_ADDRESSES,
  ].join('\n')
  // Prepend a BOM so Excel opens the UTF-8 CSV with correct encoding.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = PARTNER_WHITELIST_TEMPLATE_FILENAME
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const PartnerWhitelistUpload = ({ onUploaded }: PartnerWhitelistUploadProps) => {
  const beforeUpload = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!PARTNER_WHITELIST_ALLOWED_EXTENSIONS.includes(ext)) {
      message.error(PARTNER_REFERRAL_MESSAGES.E01)
      return Upload.LIST_IGNORE
    }
    if (file.size / 1024 / 1024 > PARTNER_WHITELIST_MAX_SIZE_MB) {
      message.error(PARTNER_REFERRAL_MESSAGES.E02)
      return Upload.LIST_IGNORE
    }
    return true
  }

  const handleUpload = async ({ file, onSuccess, onError }: UploadRequestOption) => {
    const formData = new FormData()
    formData.append('file', file as File)

    try {
      const res = await AppAdmin.uploadPartnerReferralWhitelist(formData)
      const data = res.data?.data as PartnerWhitelistUploadResponse | undefined

      if (data) {
        message.success(
          `Imported ${data.inserted}/${data.total} wallet${data.total === 1 ? '' : 's'}` +
            (data.skipped ? ` (${data.skipped} skipped)` : '')
        )
      } else {
        message.success('Whitelist uploaded successfully')
      }
      onSuccess?.('ok')
      onUploaded(data)
    } catch (error: any) {
      logError('Partner whitelist upload failed', error)
      message.error(
        error?.response?.data?.message || error?.message || 'Failed to upload whitelist'
      )
      onError?.(error)
    }
  }

  return (
    <Upload.Dragger
      name="file"
      multiple={false}
      accept={PARTNER_WHITELIST_ACCEPT}
      showUploadList={false}
      beforeUpload={beforeUpload}
      customRequest={handleUpload}
      className="partner-referral__dragger"
    >
      <Flex align="center" justify="space-between" gap={16} wrap="wrap">
        <Flex align="center" gap={16}>
          <InboxOutlined style={{ fontSize: 28, color: '#9ca3af' }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>
              Drag &amp; drop a wallet list file, or click to browse
            </div>
            <div style={{ fontSize: 12, color: '#6a7282' }}>
              Supports .csv, .xlsx, .xls — max {PARTNER_WHITELIST_MAX_SIZE_MB}MB ·{' '}
              <a
                onClick={(e) => {
                  // Don't let the click bubble up to the Dragger (which opens the file dialog).
                  e.preventDefault()
                  e.stopPropagation()
                  downloadWhitelistTemplate()
                }}
              >
                Download template
              </a>
            </div>
          </div>
        </Flex>
        <Button icon={<UploadOutlined />}>Upload File</Button>
      </Flex>
    </Upload.Dragger>
  )
}
